import React, { useContext } from 'react';

import { CondensedHeader } from '../../content/CondensedHeader/CondensedHeader';
import { Layout } from '../../layout/Layout/Layout';
import { LayoutItem } from '../../layout/Layout/LayoutItem';
import { AttrsHelper } from '../../../sb-helpers/attrs-helper';
import { backgroundColorNames } from '../../../theme/axiom-theme';
import { ColorUtil } from '../../../utils/color-util';
import type { BadgeBackgrounds } from '../Badge/Badge';
import type { ProgressGroupContextType } from '../ProgressGroup/ProgressGroupContext';
import { ProgressGroupContext } from '../ProgressGroup/ProgressGroupContext';

import { ProgressGraduations } from './ProgressGraduations';

export const ProgressBarColors = Object.values(backgroundColorNames);
export type ProgressProps = {
  className?: string;
  color: (typeof BadgeBackgrounds)[number];
  backgroundColor?: (typeof ProgressBarColors)[number];
  currentStep: number;
  name?: string;
  hideLabel?: boolean;
  showHover?: boolean;
  size?: 'default' | 'large';
  type?: 'number' | 'percent';
  totalSteps: number;
};

/**
 * TODO Bootstrap: remove rd- when we remove bootstrap stylesheet
 */
export const Progress = ({
  className,
  color,
  backgroundColor = 'light blue',
  currentStep,
  name,
  hideLabel = false,
  showHover = false,
  size = 'default',
  type = 'number',
  totalSteps,
}: ProgressProps) => {
  const { showLabel, graduations }: ProgressGroupContextType =
    useContext(ProgressGroupContext);

  // Clamp currentStep to totalSteps
  const currentStepFormatted = Math.min(currentStep, totalSteps);
  const percentComplete = (currentStepFormatted / totalSteps) * 100;

  const displayLabel = showLabel && !hideLabel;

  return (
    <Layout
      horizontalGutter="24px"
      className={AttrsHelper.formatClassname('rd-progress', className)}
      position="middle"
      name={name}
    >
      <LayoutItem fluid>
        <div
          className={AttrsHelper.formatClassname(
            'progress-bar-display',
            `size-${size}`,
            ColorUtil.nameClass({
              color: backgroundColor,
              prefix: 'background',
            })
          )}
          data-value={totalSteps}
        >
          <div
            className={AttrsHelper.formatClassname(
              'progress-bar-complete',
              ColorUtil.nameClass({ color, prefix: 'background' }),
              showHover && 'show-hover'
            )}
            style={{ width: `${percentComplete}%` }}
            data-value={currentStepFormatted}
          />
          <ProgressGraduations graduationsCount={graduations} />
        </div>
      </LayoutItem>
      {displayLabel ? (
        <LayoutItem className="progress-bar-steps">
          <CondensedHeader name="LABEL">
            {type === 'number' ? (
              <>
                <span data-test="CURRENT_PROGRESS_STEP">
                  {currentStepFormatted}
                </span>
                /<span data-test="TOTAL_PROGRESS_STEPS">{totalSteps}</span>
              </>
            ) : (
              <span data-test="CURRENT_PROGRESS_STEP">
                {Math.round(percentComplete)}%
              </span>
            )}
          </CondensedHeader>
        </LayoutItem>
      ) : null}
    </Layout>
  );
};
